import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { TrendingUp, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './CompoundInterestCalculatorPage/components/CalculationForm';
import ResultsOverview from './CompoundInterestCalculatorPage/components/ResultsOverview';
import InvestmentChart from './CompoundInterestCalculatorPage/components/InvestmentChart';
import QuickAdjustmentSidebar from './CompoundInterestCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './CompoundInterestCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './CompoundInterestCalculatorPage/components/TipsSidebar';
import InfoContent from './CompoundInterestCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  initialInvestment: '100000',
  additionalContribution: '10000',
  contributionFrequency: 'monthly', // 'monthly', 'quarterly', 'annually', 'none'
  investmentTerm: '10', // Years
  interestRate: '8.5', // Annual percentage
  compoundingFrequency: 'annually', // 'annually', 'semi_annually', 'quarterly', 'monthly', 'daily'
  inflation: '4.0', // Annual percentage
  taxRate: '13', // Percent
  reinvestDividends: true,
  investmentStartDate: new Date().toISOString().split('T')[0],
  adjustForInflation: true,
  includeAdditionalContributions: true,
};

// Определение частот пополнения
export const contributionFrequencies = [
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'semi_annually', name: 'Раз в полгода' },
  { id: 'annually', name: 'Ежегодно' },
  { id: 'none', name: 'Без пополнений' }
];

// Определение частот начисления процентов
export const compoundingFrequencies = [
  { id: 'daily', name: 'Ежедневно', periodsPerYear: 365 },
  { id: 'weekly', name: 'Еженедельно', periodsPerYear: 52 },
  { id: 'monthly', name: 'Ежемесячно', periodsPerYear: 12 },
  { id: 'quarterly', name: 'Ежеквартально', periodsPerYear: 4 },
  { id: 'semi_annually', name: 'Раз в полгода', periodsPerYear: 2 },
  { id: 'annually', name: 'Ежегодно', periodsPerYear: 1 }
];

// Определение типов инвестиций (для предварительной настройки)
export const investmentTypes = [
  { id: 'deposit', name: 'Банковский вклад', defaultRate: 7.5 },
  { id: 'bond', name: 'Облигации', defaultRate: 8.0 },
  { id: 'stock', name: 'Акции', defaultRate: 11.0 },
  { id: 'etf', name: 'ETF/ПИФы', defaultRate: 9.5 },
  { id: 'realestate', name: 'Недвижимость', defaultRate: 6.0 },
  { id: 'metals', name: 'Драгоценные металлы', defaultRate: 5.0 }
];

/**
 * CompoundInterestCalculatorPage Component - Калькулятор сложных процентов с учетом инфляции, налогов и дополнительных взносов.
 */
const CompoundInterestCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('compound-interest-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор сложных процентов | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте доходность инвестиций с учетом сложных процентов, дополнительных взносов, инфляции и налогов. Подробный анализ с графиками и таблицами для эффективного планирования финансового будущего.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [investmentData, setInvestmentData] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedInvestments, setSavedInvestments] = useState([]);
  const [showInflationAdjusted, setShowInflationAdjusted] = useState(true);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных инвестиций из localStorage ---
  useEffect(() => {
    const savedInvestmentsData = localStorage.getItem('savedInvestments');
    if (savedInvestmentsData) {
      try {
        setSavedInvestments(JSON.parse(savedInvestmentsData));
      } catch (e) {
        console.error("Error loading saved investments:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findRate = (id) => {
        const found = investmentTypes.find(t => t.id === id);
        return found ? found.defaultRate.toString() : initialFormData.interestRate;
      };

      switch (subToolId) {
        case 'deposit':
          newState.interestRate = findRate('deposit');
          newState.compoundingFrequency = 'monthly';
          break;
        case 'stock':
          newState.interestRate = findRate('stock');
          newState.compoundingFrequency = 'annually';
          break;
        case 'without-inflation':
          newState.adjustForInflation = false;
          break;
        case 'with-contributions':
          newState.includeAdditionalContributions = true;
          newState.additionalContribution = '15000';
          break;
        case 'retirement':
          newState.investmentTerm = '30';
          newState.additionalContribution = '20000';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setInvestmentData([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение инвестиции в localStorage
  const saveInvestment = (name) => {
    if (!results) return;

    const newSavedInvestment = {
      id: Date.now().toString(),
      name: name || `Инвестиция ${formData.initialInvestment}₽ на ${formData.investmentTerm} лет`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedInvestments = [...savedInvestments, newSavedInvestment];
    setSavedInvestments(updatedSavedInvestments);
    localStorage.setItem('savedInvestments', JSON.stringify(updatedSavedInvestments));

    return newSavedInvestment;
  };

  // Удаление сохраненной инвестиции
  const deleteSavedInvestment = (id) => {
    const updatedSavedInvestments = savedInvestments.filter(investment => investment.id !== id);
    setSavedInvestments(updatedSavedInvestments);
    localStorage.setItem('savedInvestments', JSON.stringify(updatedSavedInvestments));
  };

  // Загрузка сохраненной инвестиции
  const loadSavedInvestment = (id) => {
    const investmentToLoad = savedInvestments.find(investment => investment.id === id);
    if (investmentToLoad) {
      setFormData(investmentToLoad.formData);
      setResults(investmentToLoad.results);
      setInvestmentData(investmentToLoad.results.yearlyData || []);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор сложных процентов'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              investmentData={investmentData}
              setInvestmentData={setInvestmentData}
              contributionFrequencies={contributionFrequencies}
              compoundingFrequencies={compoundingFrequencies}
              saveInvestment={saveInvestment}
              investmentTypes={investmentTypes}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview 
                  results={results} 
                  formData={formData}
                />
                <InvestmentChart 
                  data={investmentData}
                  formData={formData}
                  showInflationAdjusted={showInflationAdjusted}
                  setShowInflationAdjusted={setShowInflationAdjusted}
                />
              </>
            )}

            {/* --- Информационный контент --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              investmentTypes={investmentTypes}
            />

            {/* --- Панель сравнения инвестиций --- */}
            <ComparisonSidebar 
              savedInvestments={savedInvestments}
              deleteSavedInvestment={deleteSavedInvestment}
              loadSavedInvestment={loadSavedInvestment}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .custom-scrollbar {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default CompoundInterestCalculatorPage;